import {
  Box, InputAdornment, MenuItem, Paper, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import SearchIcon from '@mui/icons-material/Search'
import { useState } from 'react'
import { type ManagementDrillLevel, type ManagementStatusFilter } from '../../../../models/peerToPeer/Management'
import { useP2PManagement } from '../../../../hooks/use-p2pManagement'
import { ScopeFilter } from '../ScopeFilter'
import { BuildingTable } from './BuildingTable'
import { DrillNode } from './DrillNode'
import { NoResults } from './NoResults'
import { SummaryTiles } from './SummaryTiles'
import { FILTER_LEVEL_OPTIONS, ROW_NODE_LEVEL, orgOptionsFor } from './managementScope'
import { styles } from './Management.styles'

interface Props { onBackToAdministration?: () => void }

export const Management = ({ onBackToAdministration }: Props): JSX.Element => {
  const [view, setView] = useState<'org' | 'status'>('org')
  const [statusFilter, setStatusFilter] = useState<ManagementStatusFilter>('All')
  const [filterLevel, setFilterLevel] = useState<ManagementDrillLevel>('State')
  const [filterOrgId, setFilterOrgId] = useState<number | 'all'>('all')
  const [search, setSearch] = useState('')

  const { overview, yearId, setYearId, activity, statusRows, searching, refresh } =
    useP2PManagement({ view, statusFilter, search, filterLevel })

  // Rows shown in the org view: all orgs at the filter level, narrowed to one when a specific
  // org is picked. Tiles reflect the picked org's own metric, or the whole scope for "All".
  const activityRows = activity?.rows ?? []
  const selectedOrgRow = filterOrgId === 'all' ? null : activityRows.find(row => row.organizationId === filterOrgId) ?? null
  const drillRows = selectedOrgRow != null ? [selectedOrgRow] : activityRows
  const metrics = (view === 'org' && selectedOrgRow != null) ? selectedOrgRow : overview
  const orgOptions = orgOptionsFor(filterLevel, activityRows)
  const orgFilter = orgOptions == null
    ? undefined
    : {
        value: String(filterOrgId),
        options: orgOptions,
        onChange: (value: string) => { setFilterOrgId(value === 'all' ? 'all' : Number(value)) },
        testId: 'p2pFilterOrg'
      }

  return (
    <Box data-testid='p2pManagement'>
      {onBackToAdministration != null &&
        <Box sx={styles.backLink} onClick={onBackToAdministration} data-testid='p2pBackToAdministration'>
          <KeyboardArrowLeftIcon fontSize='small' /><Typography variant='body2'>Back to Administration</Typography>
        </Box>}
      <Typography variant='h1' sx={styles.title}>Peer to Peer - Admin</Typography>

      <Paper elevation={0} sx={styles.panel}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ md: 'flex-start' }} mb='20px' gap='12px'>
          <Box>
            <Typography variant='h3'>Data Management</Typography>
            <Typography variant='body2' color='text.secondary'>View and edit Peer to Peer form data across all organizations.</Typography>
          </Box>
          {overview != null &&
            <Stack direction='row' alignItems='center' gap='8px'>
              <Typography variant='body2' color='text.secondary'>Select year</Typography>
              <Select size='small' value={yearId} onChange={e => { setYearId(Number(e.target.value)) }} data-testid='p2pYearSelector' sx={styles.yearSelect}>
                {overview.yearOptions.map(year => <MenuItem key={year.id} value={year.id}>{year.label}</MenuItem>)}
              </Select>
            </Stack>}
        </Stack>

        {metrics != null && <SummaryTiles metrics={metrics} />}

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ md: 'center' }} gap='12px' mb='20px'>
          <Stack direction='row' alignItems='center' gap='8px' flexWrap='wrap'>
            <ScopeFilter
              levelValue={filterLevel}
              levelOptions={FILTER_LEVEL_OPTIONS}
              onLevelChange={value => { setFilterLevel(value as ManagementDrillLevel); setFilterOrgId('all') }}
              levelTestId='p2pFilterLevel'
              org={orgFilter}
            />
          </Stack>
          <TextField
            size='small' variant='outlined' placeholder='Search by name, email, building, etc'
            value={search} onChange={e => { setSearch(e.target.value) }} sx={styles.search}
            InputProps={{ startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment> }}
            inputProps={{ 'data-testid': 'p2pStatusSearch' }}
          />
        </Stack>

        <Paper variant='outlined' sx={styles.activityCard}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb='8px'>
            <Typography variant='h4'>Form Activity</Typography>
            <ToggleButtonGroup
              exclusive size='small' value={view} data-testid='p2pViewToggle' sx={styles.viewToggle}
              onChange={(_, value) => { if (value != null) setView(value) }}
            >
              <ToggleButton value='org' data-testid='p2pViewOrg'>Organization</ToggleButton>
              <ToggleButton value='status' data-testid='p2pViewStatus'>Status</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {searching &&
            <Box data-testid='p2pSearchResults'>
              {statusRows.length > 0
                ? <BuildingTable rows={statusRows} reportingYearId={yearId} onChanged={refresh} />
                : <NoResults />}
            </Box>}

          {!searching && view === 'org' &&
            <Box data-testid='p2pDrillDown'>
              {drillRows.length > 0
                ? drillRows.map(row => <DrillNode key={`${row.organizationId}-${yearId}`} level={ROW_NODE_LEVEL[filterLevel]} row={row} yearId={yearId} />)
                : <NoResults />}
            </Box>}

          {!searching && view === 'status' &&
            <Box>
              <Stack direction='row' gap='8px' mb='12px' alignItems='center' sx={{ pt: '8px' }}>
                <ToggleButtonGroup
                  exclusive size='small' value={statusFilter} data-testid='p2pStatusFilter'
                  onChange={(_, value) => { if (value != null) setStatusFilter(value) }}
                >
                  <ToggleButton value='All'>All</ToggleButton>
                  <ToggleButton value='InProgress'>In progress</ToggleButton>
                  <ToggleButton value='Submitted'>Submitted</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {statusRows.length > 0 ? <BuildingTable rows={statusRows} reportingYearId={yearId} onChanged={refresh} /> : <NoResults />}
            </Box>}
        </Paper>
      </Paper>
    </Box>
  )
}
